// ExpandableStaffAssessment.jsx
import React from "react";
import { Button, Table, TableHead, TableRow,
         TableCell, TableBody, IconChevronDown24,
         IconInfo16, IconChevronUp24 } from "@dhis2/ui";

const ExpandableStaffAssessment = ({
  assessment,
  staff,
  index,
  isExpanded,
  onToggle,
  onApproveAll,
  onReturnAll
}) => {
  const staffName = staff ? `${staff.firstName} ${staff.surname}` : 'Unknown Staff';
  
  // ✅ Calculate ACTUAL approval status (not just pending count)
  const allApproved = assessment.competencies?.every(c => c.approvalStatus === 'approved');
  const allReturned = assessment.competencies?.every(c => c.approvalStatus === 'returned');
  const hasPending = assessment.competencies?.some(c => c.approvalStatus === 'pending' || !c.approvalStatus);
  
  let statusText = 'Pending';
  let statusColor = '#ff9800';
  if (allApproved) {
    statusText = 'Approved';
    statusColor = '#4caf50';
  } else if (allReturned) {
    statusText = 'Returned';
    statusColor = '#f44336';
  }

  const totalCount = assessment.competencies?.length || 0;
  const approvedCount = assessment.competencies?.filter(c => c.approvalStatus === 'approved').length || 0;
  const pendingCount = totalCount - approvedCount;

  const calculateScore = (comp) => {
    const filled = (comp.ratings || [null, null]).filter((r, i) => 
      r != null && (comp.ratingsLocked || [false, false])[i]
    );
    if (!filled.length) return 0;
    const avg = filled.reduce((a, b) => a + b, 0) / filled.length;
    return (comp.weight || 0) * avg;
  };

  const calculateFinalScore = () =>
    (assessment.competencies || []).reduce((sum, c) => sum + calculateScore(c), 0).toFixed(2);

  return (
    <div style={{ 
      marginBottom: '12px', 
      border: '1px solid #e0e0e0', 
      borderRadius: '8px',
      overflow: 'hidden',
      backgroundColor: 'white'
    }}>
      {/* Header */}
      <div 
        style={{ 
          padding: '16px 20px',
          backgroundColor: isExpanded ? '#f0f7ff' : '#f8f9fa',
          borderBottom: isExpanded ? '1px solid #d0e0ff' : 'none',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer'
        }}
        onClick={() => onToggle(index)}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {isExpanded ? <IconChevronUp24 /> : <IconChevronDown24 />}
          <div>
            <h4 style={{ margin: 0, color: '#2c6693' }}>{staffName}</h4>
            {assessment.positionTitle && (
              <span style={{ fontSize: '0.875rem', color: '#666' }}>
                {assessment.positionTitle}
              </span>
            )}
          </div>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {/* Status Summary */}
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '0.875rem', color: '#666' }}>
              Status: <span style={{ 
                color: statusColor,
                fontWeight: 'bold'
              }}>
                {statusText}
              </span>
            </div>
            <div style={{ fontSize: '0.75rem', color: '#888' }}>
              {approvedCount}/{totalCount} approved
            </div>
          </div>
          
          {/* Single Approve/Return Buttons */}
          {!allApproved && !allReturned && (
            <div style={{ display: 'flex', gap: '8px' }}>
              <Button 
                small 
                primary 
                onClick={(e) => {
                  if (window.confirm(`Approve ALL competencies for ${staffName}?`)) {
                    onApproveAll(assessment.teiId);
                  }
                }}
              >
                Approve
              </Button>
              <Button 
                small 
                destructive 
                onClick={(e) => {
                    if (window.confirm(`Return ALL competencies for ${staffName}?`)) {
                    onReturnAll(assessment.teiId);
                  }
                }}
              >
                Return
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div style={{ padding: '24px' }}>
          {/* Summary Stats */}
          <div style={{ 
            display: 'flex', 
            gap: '24px', 
            marginBottom: '24px',
            padding: '16px',
            backgroundColor: '#f8f9fa',
            borderRadius: '6px'
          }}>
            <div>
              <div style={{ fontSize: '0.75rem', color: '#666' }}>Total Competencies</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#2c6693' }}>
                {totalCount}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '0.75rem', color: '#666' }}>Approved</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#4caf50' }}>
                {approvedCount}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '0.75rem', color: '#666' }}>Pending/Returned</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#ff9800' }}>
                {totalCount - approvedCount}
              </div>
            </div>
          </div>

          {/* Competency Table */}
          <div style={{ 
            marginBottom: '24px',
            border: '1px solid #e0e0e0',
            borderRadius: '6px',
            overflow: 'hidden'
          }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell style={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Competency</TableCell>
                  <TableCell style={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Definition</TableCell>
                  <TableCell style={{ fontWeight: 'bold', backgroundColor: '#f8f9fa', textAlign: 'center' }}>Weight</TableCell>
                  <TableCell style={{ fontWeight: 'bold', backgroundColor: '#f8f9fa', textAlign: 'center' }}>Mid-Year</TableCell>
                  <TableCell style={{ fontWeight: 'bold', backgroundColor: '#f8f9fa', textAlign: 'center' }}>End-Year</TableCell>
                  <TableCell style={{ fontWeight: 'bold', backgroundColor: '#f8f9fa', textAlign: 'center' }}>Score</TableCell>
                  {/* ❌ REMOVED: Status column */}
                </TableRow>
              </TableHead>
              <TableBody>
                {assessment.competencies.map((comp, compIndex) => (
                  <TableRow key={compIndex}>
                    <TableCell style={{ fontWeight: '500', verticalAlign: 'top' }}>
                      {comp.name}
                    </TableCell>
                    <TableCell style={{ verticalAlign: 'top', fontSize: '0.875rem', color: '#555' }}>
                      {comp.definition}
                    </TableCell>
                    <TableCell style={{ textAlign: 'center', verticalAlign: 'top' }}>
                      {comp.weight != null ? `${(comp.weight * 100).toFixed(0)}%` : 'N/A'}
                    </TableCell>
                    <TableCell style={{ textAlign: 'center', verticalAlign: 'top' }}>
                      <div style={{ 
                        display: 'inline-block',
                        padding: '4px 12px',
                        backgroundColor: comp.ratings[0] !== null ? '#e8f5e9' : '#f5f5f5',
                        borderRadius: '4px',
                        fontWeight: '500'
                      }}>
                        {comp.ratings[0] !== null ? comp.ratings[0] : 'N/R'}
                      </div>
                      {/* 👇 READ-ONLY MANAGER FEEDBACK */}
                      {comp.comments[0] && (
                        <div style={{ 
                          marginTop: '4px',
                          padding: '4px 8px',
                          backgroundColor: '#f0f7ff',
                          borderRadius: '4px',
                          fontSize: '0.8rem',
                          fontStyle: 'italic'
                        }}>
                          {comp.comments[0]}
                        </div>
                      )}
                    </TableCell>
                    <TableCell style={{ textAlign: 'center', verticalAlign: 'top' }}>
                      <div style={{ 
                        display: 'inline-block',
                        padding: '4px 12px',
                        backgroundColor: comp.ratings[1] !== null ? '#e8f5e9' : '#f5f5f5',
                        borderRadius: '4px',
                        fontWeight: '500'
                      }}>
                        {comp.ratings[1] !== null ? comp.ratings[1] : 'N/R'}
                      </div>
                      {/* 👇 READ-ONLY MANAGER FEEDBACK */}
                      {comp.comments[1] && (
                        <div style={{ 
                          marginTop: '4px',
                          padding: '4px 8px',
                          backgroundColor: '#f0f7ff',
                          borderRadius: '4px',
                          fontSize: '0.8rem',
                          fontStyle: 'italic'
                        }}>
                          {comp.comments[1]}
                        </div>
                      )}
                    </TableCell>
                    <TableCell style={{ textAlign: 'center', verticalAlign: 'top', fontWeight: 'bold' }}>
                      {calculateScore(comp).toFixed(2)}
                    </TableCell>
                    {/* ❌ NO STATUS COLUMN HERE */}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* ✅ MANAGER FEEDBACK SECTION (BOTTOM OF FORM) */}
             {/* ✅ Unified Manager Feedback (only if all remarks are the same) */}
      {assessment.competencies.some(c => c.approvalRemark) && (
        <div style={{
          marginTop: '24px',
          padding: '16px',
          backgroundColor: '#e8f4fd',
          borderRadius: '8px',
          border: '1px solid #b3d9ff'
        }}>
          <h5 style={{ 
            color: '#2c6693', 
            marginBottom: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <IconInfo16 />
            Manager's Feedback
          </h5>

          {/* Check if all non-empty remarks are identical */}
          {(() => {
            const remarks = assessment.competencies
              .map(c => c.approvalRemark)
              .filter(r => r?.trim());

      const uniqueRemarks = [...new Set(remarks)];
      const allSame = uniqueRemarks.length === 1 && remarks.length > 0;

      if (allSame) {
        // ✅ Single message
        return (
          <div style={{ 
            padding: '12px', 
            backgroundColor: '#fff',
            borderRadius: '6px',
            border: '1px solid #d0e0ff'
          }}>
            <strong>Status:</strong> {statusText}<br />
            <em>“{uniqueRemarks[0]}”</em>
          </div>
        );
      } else {
        // ❌ Mixed remarks → show per competency (fallback)
              return assessment.competencies.map((comp, idx) =>
                comp.approvalRemark ? (
                  <div key={idx} style={{ 
                    marginBottom: '12px', 
                    padding: '12px', 
                    backgroundColor: '#fff',
                    borderRadius: '6px',
                    border: '1px solid #d0e0ff'
                  }}>
                    <strong>{comp.name}:</strong> "{comp.approvalRemark}"
                  </div>
                ) : null
              );
            }
          })()}
        </div>
)}
      
        </div>
      )}
    </div>
  );
};

export default ExpandableStaffAssessment;